import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { LoggingService } from '@ld.consumer-portal/utils';
import { firstValueFrom } from 'rxjs';

interface MlaEmailStatusResponse {
  success: boolean;
  data?: string;
}

interface MlaSendPasswordResetResponse {
  success: boolean;
}

interface MlaUpdatePasswordResponse {
  success: boolean;
  data?: { email: string; multiFactor?: boolean };
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MlaPasswordService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  async checkEmailStatus(email: string): Promise<string> {
    const encodedEmail = encodeURIComponent(email);
    const url = `${this.mlaApiUrl}api/v1/user/email?email=${encodedEmail}`;

    try {
      const response = await firstValueFrom(this.http.get<MlaEmailStatusResponse>(url));
      return response?.data ?? 'NotExist';
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaPasswordService] checkEmailStatus error');
      return 'NotExist';
    }
  }

  async sendPasswordResetEmail(email: string): Promise<boolean> {
    const encodedEmail = encodeURIComponent(email);
    const url = `${this.mlaApiUrl}api/v1/password/reset?email=${encodedEmail}`;

    try {
      const response = await firstValueFrom(
        this.http.post<MlaSendPasswordResetResponse>(url, {}),
      );
      return response?.success ?? false;
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaPasswordService] sendPasswordResetEmail error');
      return false;
    }
  }

  async updatePasswordWithToken(
    passwordToken: string,
    newPassword: string,
  ): Promise<MlaUpdatePasswordResponse> {
    const url = `${this.mlaApiUrl}api/v1/password/token`;
    const body = { passwordToken, newPassword, login: false };

    try {
      const response = await firstValueFrom(this.http.patch<MlaUpdatePasswordResponse>(url, body));
      return response ?? { success: false };
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaPasswordService] updatePasswordWithToken error');
      const httpError = error as { error?: { message?: string }; message?: string };
      const message = httpError.error?.message ?? httpError.message ?? 'An unexpected error occurred';
      return { success: false, message };
    }
  }
}
